import { isEmpty } from 'lodash';
import Lang from '/@/modules/core/app/services/Lang';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { EstateAccountMeta } from '/@/modules/estateaccount/models/meta/EstateAccountMeta';
import { PredefinedEstateRole } from '/@/modules/role/enums/PredefinedEstateRole';
import { EstateRelationshipType } from '/@/modules/estate/enums/EstateRelationshipType';
import { BeneficiaryCategory } from '/@/modules/beneficiary/enums/BeneficiaryCategory';
import { BeneficiaryAddedStage } from '/@/modules/beneficiary/enums/BeneficiaryAddedStage';
import { BeneficiaryGuardianCategory } from '/@/modules/beneficiary/enums/BeneficiaryGuardianCategory';
import { ReasonForExecutorship } from '@/modules/estateaccount/models/meta/constants/ReasonForExecutorship';
import { User } from '/@/modules/user/models/User';
import { EstateRole } from '/@/modules/role/models/EstateRole';
import { EstateDistribution } from '/@/modules/estatedistribution/models/EstateDistribution';
import { ModelHasAsset } from '/@/modules/contact/models/ModelHasAsset';
import { FinancialAccount } from '/@/modules/financialaccount/models/FinancialAccount';
import { EsignEnvelopeEstateAccount } from '/@/modules/esign/models/EsignEnvelopeEstateAccount';
import { EstateDistributionPayment } from '/@/modules/estatedistribution/models/EstateDistributionPayment';
import { PlatformReferral } from '/@/modules/platformreferral/models/PlatformReferral';
import { Estate } from '/@/modules/estate/models/Estate';
import { ModelHasLiability } from '@/modules/contact/models/ModelHasLiability';
import { DocumentCollection } from '/@/modules/document/models/DocumentCollection';
import { DefaultAccountMeta } from '@/modules/estateaccount/models/meta/DefaultAccountMeta';
import { EstateProfessionalAccountMeta } from '@/modules/estateaccount/models/meta/EstateProfessionalAccountMeta';
import { ExecutorAccountMeta } from '@/modules/estateaccount/models/meta/ExecutorAccountMeta';
import { PlannerAccountMeta } from '@/modules/estateaccount/models/meta/PlannerAccountMeta';
import { ProfessionalAccountMeta } from '@/modules/estateaccount/models/meta/ProfessionalAccountMeta';
import { BaseBeneficiaryMeta } from '@/modules/estateaccount/models/meta/BaseBeneficiaryMeta';
import { BeneficiaryOrganizationMeta } from '@/modules/estateaccount/models/meta/BeneficiaryOrganizationMeta';
import { EstateDistributionType } from '@/modules/estatedistribution/enums/EstateDistributionType';
import { EstateDistributionRecipientType } from '/@/modules/estatedistribution/enums/EstateDistributionRecipientType';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import NumeralService from '@/modules/core/app/services/NumeralService';
import { Referral } from '@/modules/referral/models/Referral';

export class EstateAccount extends BaseEntityModel {
  id?: number;
  estateId?: number;
  roleId?: number;
  userId?: number;
  isActive?: boolean;
  lastLoginAt?: string;
  meta:
    | EstateProfessionalAccountMeta
    | BaseBeneficiaryMeta
    | ExecutorAccountMeta
    | ProfessionalAccountMeta
    | PlannerAccountMeta
    | DefaultAccountMeta
    | BeneficiaryOrganizationMeta
    | null;
  hasDependants?: boolean;
  documents: Relationship<DocumentCollection | []>;
  user: Relationship<User> | null;
  estate: Relationship<Estate> | null;
  alternateBeneficiaries: Relationship<User[]> | null;
  alternateExecutors: Relationship<User[]> | null;
  estateRole: Relationship<EstateRole> | null;
  estateDistributions: Relationship<EstateDistribution[]> | null;
  alternateDistributions: Relationship<EstateDistribution[]> | null;
  alternateGuardianUsers: Relationship<User[]> | null;
  guardianUsers: Relationship<User[]> | null;
  careGiverUsers: Relationship<User[]> | null;
  pivot: Relationship<ModelHasAsset | ModelHasLiability> | null;
  financialAccount: Relationship<FinancialAccount> | null;
  esignEnvelopeEstateAccounts: Relationship<EsignEnvelopeEstateAccount[]> | null;
  estateDistributionPayments: Relationship<EstateDistributionPayment[]> | null;
  platformReferral?: Relationship<PlatformReferral> | null;

  constructor(model: Partial<EstateAccount> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.userId = model.userId;
    this.isActive = model.isActive;
    this.meta = model.meta as typeof this.meta; // properly built in setRoleId
    this.setRoleId(model.roleId);
    this.hasDependants = model.hasDependants;
    this.lastLoginAt = model.lastLoginAt;

    this.user = User.mapModelOrNull(model.user);
    this.estate = Estate.mapModelOrNull(model.estate);
    this.estateRole = EstateRole.mapModelOrNull(model.estateRole);
    this.estateDistributions = EstateDistribution.mapModelsOrNull(model.estateDistributions);
    this.alternateDistributions = EstateDistribution.mapModelsOrNull(model.alternateDistributions);
    this.documents = DocumentCollection.mapModel(model.documents);
    this.pivot =
      model.pivot && Object.keys(model.pivot).includes('liabilityId')
        ? ModelHasLiability.mapModelOrNull(model.pivot as ModelHasLiability)
        : ModelHasAsset.mapModelOrNull(model.pivot);
    this.alternateGuardianUsers = User.mapModelsOrNull(model.alternateGuardianUsers);
    this.guardianUsers = User.mapModelsOrNull(model.guardianUsers);
    this.careGiverUsers = User.mapModelsOrNull(model.careGiverUsers);
    this.alternateBeneficiaries = User.mapModelsOrNull(model.alternateBeneficiaries);
    this.alternateExecutors = User.mapModelsOrNull(model.alternateExecutors);
    this.financialAccount = FinancialAccount.mapModelOrNull(model.financialAccount);
    this.esignEnvelopeEstateAccounts = EsignEnvelopeEstateAccount.mapModelsOrNull(
      model.esignEnvelopeEstateAccounts
    );
    this.estateDistributionPayments = EstateDistributionPayment.mapModelsOrNull(
      model.estateDistributionPayments
    );
    this.platformReferral = PlatformReferral.mapModelOrNull(model.platformReferral);
  }

  getModelName(): string {
    return ModelType.ESTATE_ACCOUNT;
  }

  setRoleId(value?: number): void {
    this.roleId = value;
    this.meta = EstateAccountMeta.factory(this.roleId, this.meta ?? {});
  }

  addRoleId(value: number): void {
    const values: [number, number | undefined] = [value, this.roleId];
    let roleId: number = value;

    if (
      values.includes(PredefinedEstateRole.BENEFICIARY.id) &&
      values.includes(PredefinedEstateRole.EXECUTOR.id)
    ) {
      roleId = PredefinedEstateRole.EXECUTOR_AND_BENEFICIARY.id;
    } else if (
      values.includes(PredefinedEstateRole.DEPENDANT.id) &&
      values.includes(PredefinedEstateRole.EXECUTOR.id)
    ) {
      roleId = PredefinedEstateRole.EXECUTOR_AND_DEPENDANT.id;
    }

    this.meta = {
      category: BeneficiaryCategory.ADULT.key,
      ...(this.meta ?? {})
    };

    this.setRoleId(roleId);
  }

  setAddedAsBeneficiaryInStage(value?: string): void {
    this.meta && ((this.meta as BaseBeneficiaryMeta).addedAsBeneficiaryIn = value);
  }

  /**
   * convert the dependant or executor account to beneficiary account
   */
  convertToBeneficiaryAccount(): void {
    this.isExecutor
      ? this.setRoleId(PredefinedEstateRole.EXECUTOR_AND_BENEFICIARY.id)
      : this.setRoleId(PredefinedEstateRole.BENEFICIARY.id);
  }

  rebuildMeta(): void {
    this.meta = EstateAccountMeta.factory(this.roleId, this.meta ?? undefined);
  }

  setDefaultRelationshipType(): void {
    (this.meta as { relationshipType?: string }).relationshipType =
      (this.meta as { relationshipType?: string }).relationshipType ??
      EstateRelationshipType.OTHER.key;
  }

  get roleName(): string | undefined {
    return this.roleId ? PredefinedEstateRole.getByKey(this.roleId, 'id')!.value : undefined;
  }

  get isOnlyExecutor(): boolean {
    return this.roleId ? PredefinedEstateRole.isOnlyExecutor(this.roleId) : false;
  }

  get isExecutor(): boolean {
    return this.roleId ? PredefinedEstateRole.isExecutor(this.roleId) : false;
  }

  get isOnlyBeneficiary(): boolean {
    return this.roleId ? PredefinedEstateRole.isOnlyBeneficiary(this.roleId) : false;
  }

  get isBeneficiary(): boolean {
    return this.roleId ? PredefinedEstateRole.isBeneficiary(this.roleId) : false;
  }

  get isLegalGuardian(): boolean {
    return !!this.hasDependants;
  }

  get isExecutorAndBeneficiary(): boolean {
    return this.roleId ? PredefinedEstateRole.isExecutorAndBeneficiary(this.roleId) : false;
  }

  get isDependant(): boolean {
    return this.roleId ? PredefinedEstateRole.isDependant(this.roleId) : false;
  }

  get isExecutorAndDependant(): boolean {
    return this.roleId ? PredefinedEstateRole.isExecutorAndDependant(this.roleId) : false;
  }

  get isEstateMember(): boolean {
    return this.roleId ? PredefinedEstateRole.isEstateMember(this.roleId) : false;
  }

  get isOrganization(): boolean {
    return BeneficiaryCategory.isOrganization((this.meta as BaseBeneficiaryMeta)?.category);
  }

  get isAdultBeneficiary(): boolean {
    return (
      this.isBeneficiary && BeneficiaryCategory.isAdult((this.meta as BaseBeneficiaryMeta).category)
    );
  }

  get isAdultBeneficiaryUnderGuardianship(): boolean {
    return (
      this.isAdultBeneficiary && !!(this.meta as { hasLegalGuardian?: boolean }).hasLegalGuardian
    );
  }

  get isChildBeneficiaryUnderGuardianship(): boolean {
    return (
      this.isBeneficiary && BeneficiaryCategory.isChild((this.meta as BaseBeneficiaryMeta).category)
    );
  }

  get isBeneficiaryUnderGuardianship(): boolean {
    return this.isAdultBeneficiaryUnderGuardianship || this.isChildBeneficiaryUnderGuardianship;
  }

  get isOrganizationBeneficiary(): boolean {
    return this.isBeneficiary && this.isOrganization;
  }

  get isProfessional(): boolean {
    return this.roleId ? PredefinedEstateRole.isProfessional(this.roleId) : false;
  }

  get hasProfessionalRole(): boolean {
    return this.roleId ? PredefinedEstateRole.hasProfessionalRole(this.roleId) : false;
  }

  get isEstateProfessional(): boolean {
    return this.roleId ? PredefinedEstateRole.isEstateProfessional(this.roleId) : false;
  }

  get isExecutorProfessional(): boolean {
    return this.roleId ? PredefinedEstateRole.isExecutorProfessional(this.roleId) : false;
  }

  get isPrimaryEstateProfessional(): boolean {
    return this.roleId
      ? this.isEstateProfessional && (this.meta as EstateProfessionalAccountMeta).isPrimary
      : false;
  }

  get isPlanner(): boolean {
    return this.roleId ? PredefinedEstateRole.isPlanner(this.roleId) : false;
  }

  get isDefault(): boolean {
    return this.roleId ? PredefinedEstateRole.isDefault(this.roleId) : false;
  }

  get isPerson(): boolean {
    return BeneficiaryCategory.isPerson((this.meta as BaseBeneficiaryMeta)?.category);
  }

  get isRelatedExecutorProfessional(): boolean {
    return (
      (this.meta as { relationshipType?: string })?.relationshipType ===
      EstateRelationshipType.EXECUTOR_PROFESSIONAL.key
    );
  }

  get isPartner(): boolean {
    return (
      this.isEstateMember &&
      ((this.meta as { relationshipType?: string }).relationshipType ===
        EstateRelationshipType.SPOUSE.key ||
        (this.meta as { relationshipType?: string }).relationshipType ===
          EstateRelationshipType.COMMON_LAW_PARTNER.key)
    );
  }

  get isSpouse(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.SPOUSE.key
    );
  }

  get isCommonLawPartner(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.COMMON_LAW_PARTNER.key
    );
  }

  get isFormerPartner(): boolean {
    return (
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.FORMER_SPOUSE.key ||
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.FORMER_COMMON_LAW_PARTNER.key
    );
  }

  get isChild(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.CHILD.key
    );
  }

  get isParent(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.PARENT.key
    );
  }

  get isSibling(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.SIBLING.key
    );
  }

  get isGrandChild(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.GRAND_CHILD.key
    );
  }

  get isNieceOrNephew(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.NIECE_OR_NEPHEW.key
    );
  }

  get isFriend(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.FRIEND.key
    );
  }

  get isOther(): boolean {
    return (
      this.isEstateMember &&
      (this.meta as { relationshipType?: string })?.relationshipType ===
        EstateRelationshipType.OTHER.key
    );
  }

  get isUnderPublicGuardianship(): boolean {
    return BeneficiaryGuardianCategory.isUnderPublicGuardianship(
      (this.meta as { guardianCategory?: string })?.guardianCategory
    );
  }

  get isUnderPrivateGuardianship(): boolean {
    return BeneficiaryGuardianCategory.isUnderPrivateGuardianship(
      (this.meta as { guardianCategory?: string })?.guardianCategory
    );
  }

  get isAddedInDistribution(): boolean {
    return (
      BeneficiaryAddedStage.isAddedInDistribution(
        (this.meta as BaseBeneficiaryMeta)?.addedAsBeneficiaryIn
      ) ?? false
    );
  }

  get displayRelationshipType(): string {
    return (this.meta as { relationshipType?: string })?.relationshipType
      ? EstateRelationshipType.getByKey(
          (this.meta as { relationshipType: string }).relationshipType
        )!.value
      : Lang.t('estateaccount.beneficiary');
  }

  get displayContactRelationshipType(): string {
    const relationshipType: string = this.displayRelationshipType;

    if (
      (this.meta as { relationshipType: string }).relationshipType ===
      EstateRelationshipType.OTHER.key
    ) {
      return `${relationshipType} - ${
        (this.meta as { otherRelationshipType: string }).otherRelationshipType ?? ''
      }`;
    }

    return relationshipType;
  }

  get userDisplayName(): string | undefined {
    if (this.user?.name) {
      return this.user.name;
    }

    const relationshipTypeLabel: string | undefined = EstateRelationshipType.getByKey(
      (this.meta as { relationshipType: string }).relationshipType
    )?.value;

    if (relationshipTypeLabel) {
      return `${relationshipTypeLabel} User`;
    }

    return 'User';
  }

  get userDisplayNameAndPartnerRelationship(): string {
    if (this.user?.name) {
      let relationship: string;
      if (this.isSpouse) {
        relationship = Lang.t('estateaccount.d187481d-3ca3-478f-9c1d-5d1dffb139d7');
      } else {
        relationship = this.isCommonLawPartner ? Lang.t('estateaccount.partner') : '';
      }

      return `${this.user.name} ${relationship}`;
    }

    const relationshipTypeLabel: string | undefined = EstateRelationshipType.getByKey(
      (this.meta as { relationshipType: string }).relationshipType
    )?.value;

    if (relationshipTypeLabel) {
      return `${relationshipTypeLabel} User`;
    }

    return `User`;
  }

  get organizationName(): string | null {
    return this.isOrganization
      ? (this.meta as BeneficiaryOrganizationMeta)?.organizationName
      : null;
  }

  get organizationFirstLetter(): string {
    return this.organizationName ? this.organizationName.charAt(0) : '';
  }

  get hasGuardians(): boolean {
    return !!this.guardianUsers?.length;
  }

  get canSearchGlobalAddress(): boolean {
    return this.isBeneficiary || this.isExecutor || this.isPlanner;
  }

  get displayMemberType(): string | undefined {
    return this.user ? this.user.displayMemberType : this.displayRelationshipType;
  }

  get isReasonForExecutorshipRenunciation(): boolean {
    return (
      (this.meta as ExecutorAccountMeta).reasonForExecutorship ===
      ReasonForExecutorship.RENUNCIATION.key
    );
  }

  get isReasonForExecutorshipDeath(): boolean {
    return (
      (this.meta as ExecutorAccountMeta).reasonForExecutorship === ReasonForExecutorship.DEATH.key
    );
  }

  get isUnableToActAsPersonalRepresentative(): boolean {
    return (
      (this.meta as ExecutorAccountMeta).willActAsPersonalRepresentative === false &&
      !!(this.meta as ExecutorAccountMeta).reasonForExecutorship
    );
  }

  get willNotActAsPersonalRepresentative(): boolean {
    return this.isUnableToActAsPersonalRepresentative || this.isReasonForExecutorshipRenunciation;
  }

  get areOrganizationDetailsComplete(): boolean {
    return (
      !isEmpty((this.meta as BeneficiaryOrganizationMeta)?.organizationName) &&
      !isEmpty((this.meta as BeneficiaryOrganizationMeta)?.registrationNumber)
    );
  }

  get isResidualBeneficiary(): boolean {
    return (
      !this.estateDistributions?.length ||
      !!this.estateDistributions?.some((dist: EstateDistribution) => dist.isTypeResidual)
    );
  }

  get isSpecificBeneficiary(): boolean {
    return !!this.estateDistributions?.some((dist: EstateDistribution) => dist.isTypeSpecific);
  }

  get beneficiaryTypeDisplay(): string {
    return this.isResidualBeneficiary
      ? EstateDistributionType.RESIDUAL.value
      : EstateDistributionType.SPECIFIC.value;
  }

  setDistributionsByType(distributionType: string): void {
    this.estateDistributions =
      this.estateDistributions?.filter(
        (estateDistribution: EstateDistribution) =>
          estateDistribution.distributionType === distributionType
      ) ?? [];
  }

  setResidualDistributions(): void {
    this.setDistributionsByType(EstateDistributionType.RESIDUAL.key);
    if (!this.estateDistributions?.length) {
      this.estateDistributions = [
        this.getNewDistribution({ distributionType: EstateDistributionType.RESIDUAL.key })
      ];
    }
  }

  getNewDistribution(model: Partial<EstateDistribution>): EstateDistribution {
    return EstateDistribution.mapModel({
      ...model,
      estateId: this.estateId,
      estateAccountId: this.id,
      plannedAmount: 0
    });
  }

  setAlternateResidualDistributions(): void {
    if (!this.alternateDistributions?.length) {
      this.alternateDistributions = [
        EstateDistribution.mapModel({
          estateId: this.estateId,
          estateAccountId: this.id,
          recipientType: EstateDistributionRecipientType.ALTERNATE.key,
          distributionType: EstateDistributionType.RESIDUAL.key,
          sharePercentage: 0
        })
      ];
    }
  }

  get setOffAmount(): number {
    return (this.totalPlannedDistributionAmount ?? 0) - (this.totalDistributionAmount ?? 0);
  }

  get distributionTypesAvailableDisplay(): string {
    return this.distributionTypesAvailable.join(', ');
  }

  get distributionTypesAvailable(): string[] {
    return this.estateDistributions
      ?.map((distribution: EstateDistribution) => Lang.t(distribution.distributionType))
      .reduce(
        (unique: string[], distributionType: string) =>
          unique.includes(distributionType) ? unique : [...unique, distributionType],
        []
      );
  }

  get totalDistributionAmount(): number | undefined {
    return this.estateDistributions?.reduce(
      (acc: number, dist: EstateDistribution) => acc + Number(dist.actualAmount) || 0,
      0
    );
  }

  get totalPlannedDistributionAmount(): number | undefined {
    return this.estateDistributions?.reduce(
      (acc: number, dist: EstateDistribution) => acc + Number(dist.plannedAmount) || 0,
      0
    );
  }

  get specificDistributions(): EstateDistribution[] | undefined {
    return this.estateDistributions?.filter((dist: EstateDistribution) => dist.isTypeSpecific);
  }

  get residualDistributions(): EstateDistribution[] | undefined {
    return this.estateDistributions?.filter((dist: EstateDistribution) => !dist.isTypeSpecific);
  }

  get totalDistributionAmountDisplay(): string {
    return NumeralService.formatValue(
      this.totalDistributionAmount,
      ColumnFormat.FORMAT_CURRENCY,
      2
    );
  }

  get totalPlannedDistributionAmountDisplay(): string {
    return NumeralService.formatValue(
      this.totalPlannedDistributionAmount,
      ColumnFormat.FORMAT_CURRENCY,
      2
    );
  }

  get name(): string | undefined {
    return (
      this.organizationName ??
      this.user?.contact?.primaryContactAlias?.fullName ??
      this.user?.contact?.fullName
    );
  }

  isVisibleInReferralAdvisorReviewInputPage(referral: Referral): boolean {
    return (
      this.userId !== referral.user?.id &&
      this.userId !== referral.partnerReferral?.user?.id &&
      !this.isProfessional
    );
  }
}
